import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, styled } from '@mui/material';

import { Column } from '../../../../../../shared/components/table/components/common.components';
import { Table } from '../../../../../../shared/components/table/table.component';
import { ConfirmationPopup } from '../../../../../../shared/components/popupTemplates/cofirmationPopup/confirmation.popup';
import { storageHelper } from '../../../../../../shared/helpers/storage';
import { localeFormatterHelper } from '../../../../../../shared/helpers/formatter/localeFormatter.helper';
import { isEnterKey } from '../../../../../../shared/helpers/utils/utils.helper';
import { CustomerCRMNote } from '../../../../services/customer.service';
import { ITabState } from '../../../../states/tabState.model';
import { crmTabState } from '../crm.state';
import { AddOrEditNote } from '../popups/addOrEditNote/addOrEditNote.popup';
import { ControlButtons } from './controlButtons.component';

export const CRMNotesTable: FC<ICRMNotesTable> = ({ crmNotes, customerId }) => {
  const { t } = useTranslation();
  const [tableData, setTableData] = useState(crmNotes);
  const [selectedNote, setSelectedNote] = useState<CustomerCRMNote | null>(null);
  const addOrEditNotePopup = useMemo(() => new AddOrEditNote(), []);
  const deleteNotePopup = useMemo(() => new ConfirmationPopup(), []);

  useEffect(() => {
    crmTabState.pub.initCRMNotesData(tableData, customerId);
    const crmTabStateSub = crmTabState.sub
      .state()
      .subscribe(({ customerCRMNotes, savedNoteId }) => {
        setTableData(customerCRMNotes);
        /* Keep the row the save just wrote selected, the way the Windows toolbar leaves
        the focus on the record SmartRefresh brought back; drop the selection otherwise. */
        setSelectedNote(
          customerCRMNotes.find((note) => String(note.id) === String(savedNoteId)) ?? null,
        );
      });

    addOrEditNotePopup.stream.state().subscribe((v) => {
      if (v?.action === 'save' && v?.values?.note) {
        crmTabState.pub.saveRecord({
          // No id yet means the popup was opened empty, which is the insert mode.
          ...(v.values.note.id && { id: v.values.note.id }),
          note: v.values.note.note ?? '',
        });
      }
    });

    return () => {
      crmTabStateSub.unsubscribe();
      addOrEditNotePopup.stream.unsubscribe();
    };
  }, []);

  return (
    <>
      <Box>
        <ControlButtons {...{ selectedNote, addOrEditNotePopup, deleteNotePopup }} />
        <STable
          data={tableData}
          initialState={{
            columns: storageHelper.local.getItem('customer.crmNotesColumnModel'),
          }}
          onChangeColumnModel={(v) =>
            storageHelper.local.setItem('customer.crmNotesColumnModel', v)
          }
          onRowClick={({ row }) => setSelectedNote(row)}
          // Double-click opens the editor, as the Windows grid does (MainGridDBCellDblClick).
          onRowDoubleClick={({ row }) =>
            addOrEditNotePopup.stream.emit('open', { values: { note: row } })
          }
          onCellKeyDown={({ row }, event) => {
            if (isEnterKey(event)) {
              addOrEditNotePopup.stream.emit('open', { values: { note: row } });
            }
          }}
          height={400}
        >
          <Column
            field='addedOn'
            headerName={t('common.date_time')}
            sortable={false}
            width={170}
            renderCell={({ value }) =>
              value
                ? localeFormatterHelper.formatDate(value, {
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                  })
                : ''
            }
          />
          <Column field='userName' headerName={t('common.username')} sortable={false} width={220} />
          <Column field='note' headerName={t('common.note')} sortable={false} flex={1} />
        </STable>
      </Box>
      {<addOrEditNotePopup.Component />}
      {<deleteNotePopup.Component />}
    </>
  );
};

const STable = styled(Table)({
  '& .MuiDataGrid-cell': {
    padding: 0,
    display: 'flex',
    alignItems: 'center',
    paddingLeft: 10,
    paddingRight: 10,
    justifyContent: 'inherit',
  },
});

interface ICRMNotesTable {
  crmNotes: ITabState['customerCRMNotes'];
  customerId: string;
}
